'use client';

import React from 'react';
import styled from 'styled-components';
import { mqXLargeMin, mqLargeMin } from 'styled/tools/media-queries';
import ParallaxBackground from 'components/reusable/ParallaxBackground/parallax-background';
import { PRODUCT_SKUS } from 'lib/constants/product-skus';
import type { CompareChartModule } from 'components/pdp/modules/types';

const CompareWrapper = styled.div`
  position: relative;
  padding: 3.5rem 0rem 1.5rem;
  overflow: hidden;

  @media ${mqLargeMin} {
    padding: 5rem 3.5rem;
  }

  * {
    color: var(--text-inverse-primary);
  }
`;

const CompareContents = styled.div`
  margin: auto;
  max-width: 90rem;
  display: flex;
  flex-direction: column;
  gap: 3rem;

  @media (${mqXLargeMin}) {
    flex-direction: row;
    justify-content: center;
  }
`;

const CompareChart = styled.table<{ $isSynWk?: boolean }>`
  position: relative;
  margin: 0 1rem;
  flex-grow: 1;
  border-radius: 2rem;
  padding: 1.5rem 2rem;
  border: 1px solid var(--outline-light-20);
  min-width: 45rem;
  width: calc(100% - 2rem);
  background: var(--color-primary-snow-white-10);
  backdrop-filter: blur(40px);
  -webkit-backdrop-filter: blur(40px);
  align-items: center;

  ${({ $isSynWk }) =>
    $isSynWk &&
    `
    padding-left: 1rem;
    padding-right: 1rem;
    min-width: unset;
  `}

  @media ${mqLargeMin} {
    margin: 0;
    width: fit-content;
    min-width: 51rem;
  }
`;

const CompareChartScrollWrapper = styled.div`
  padding-bottom: 2rem;
  overflow: auto;

  &::-webkit-scrollbar {
    width: 0.5rem;
    height: 0.5rem;
  }

  &::-webkit-scrollbar-track {
    border: 0;
    margin-left: calc(100vw - 5rem);
    margin-right: 2rem;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 4px;
    background-color: var(--fill-primary);
  }

  &::-webkit-scrollbar-track-piece {
    border-radius: 4px;
    width: 1rem;
    background: var(--outline-light-20);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
  }
`;

const CompareChartColumnTitle = styled.th<{ $isSynWk?: boolean }>`
  padding: 0 1rem;
  width: 10.5rem;
  min-width: 10.5rem;
  text-align: center;
  font-weight: 350;

  ${({ $isSynWk }) =>
    $isSynWk &&
    `
    width: 5rem;
    min-width: 5rem;
  `}

  @media ${mqLargeMin} {
    width: 10.5rem;
    min-width: 10.5rem;
  }
`;

const CompareChartColumnTitleHighlighted = styled(CompareChartColumnTitle)<{
  $isSynWk?: boolean;
}>`
  padding: 0;
  width: 6.25rem;
  min-width: 6.25rem;

  ${({ $isSynWk }) =>
    $isSynWk &&
    `
    width: 5rem;
    min-width: 5rem;
  `}

  span {
    border-radius: 440.009px;
    border: 1.32px solid var(--outline-light);
    padding: 6px;
    font-size: 14.08px;
    font-style: normal;
    font-weight: 350;
    line-height: 100%; /* 14.08px */
    letter-spacing: -0.282px;
  }

  &::before {
    content: '';
    display: block;
    position: absolute;
    top: 1.5rem;
    bottom: 1.5rem;
    border-radius: 1rem;
    width: 6.25rem;
    background-color: var(--products-dm-02-primary);
    opacity: 0.25;
    z-index: -1;

    ${({ $isSynWk }) =>
      $isSynWk &&
      `
      width: 5rem;
      min-width: 5rem;
    `}
  }

  @media ${mqLargeMin} {
    &::before {
      top: 1rem;
      width: 6.25rem;

      ${({ $isSynWk }) =>
        $isSynWk &&
        `
        width: 7rem;
      `}
    }
  }
`;

const CompareTitle = styled.h2`
  margin: 0 1rem;
  font-size: 2rem;
  font-weight: 350;
  line-height: 110%;
  letter-spacing: -0.64px;

  @media ${mqLargeMin} {
    max-width: 19rem;
    margin: 0;
    font-size: 3rem;
    letter-spacing: -0.96px;
  }
`;

const CompareChartTitle = styled.th<{ $isSynWk?: boolean }>`
  padding: 1.5rem 0;
  width: 15.625rem;
  text-align: left;
  font-weight: 350;

  ${({ $isSynWk }) =>
    $isSynWk &&
    `
    padding: 0.5rem 0;
    padding-right: 0.25rem;
  `}

  @media ${mqLargeMin} {
    padding: 1.5rem 0;
    width: 20rem;
  }
`;

const CompareChartDot = styled.div`
  margin: auto;
  display: flex;
  align-items: center;
  justify-items: center;

  &::before {
    content: '';
    margin: auto;
    border-radius: 100%;
    border: 1.5px solid var(--outline-light);
    width: 1rem;
    height: 1rem;
  }

  &.checked::before {
    background-color: var(--fill-primary);
  }
`;

const ChartItemText = styled.div<{ $isSynWk?: boolean }>`
  margin: auto;
  display: block;
  max-width: 140px;
  padding: 0 0.25rem;
  text-align: center;
  font-size: 14px;
  line-height: 19.6px;
  color: var(--text-inverse-primary);

  ${({ $isSynWk }) =>
    $isSynWk &&
    `
    width: 116px;
  `}
`;

type ProductCompareChartProps = {
  productSku?: string | false;
  compare: CompareChartModule;
  productInfo: { shortTitle: string };
};

const ProductCompareChart = ({
  productSku = false,
  compare,
  productInfo,
}: ProductCompareChartProps) => {
  if (!compare) {
    return null;
  }

  const includeThirdRow = !!compare.competitorTwoTitle;

  return (
    <CompareWrapper>
      <ParallaxBackground mediaItem={compare.backgroundImage} />
      <CompareContents>
        <CompareTitle>How {productInfo.shortTitle} Compares:</CompareTitle>
        <CompareChartScrollWrapper>
          <CompareChart $isSynWk={productSku === PRODUCT_SKUS.DAILY_SYNBIOTIC}>
            <tbody>
              <tr>
                <th aria-label='Product Compare Chart'></th>
                <CompareChartColumnTitleHighlighted
                  $isSynWk={productSku === PRODUCT_SKUS.DAILY_SYNBIOTIC}
                >
                  <span>{compare.productColumnTitle}</span>
                </CompareChartColumnTitleHighlighted>
                <CompareChartColumnTitle
                  $isSynWk={productSku === PRODUCT_SKUS.DAILY_SYNBIOTIC}
                >
                  {compare.competitorOneTitle}
                </CompareChartColumnTitle>
                {includeThirdRow && (
                  <CompareChartColumnTitle>
                    {compare.competitorTwoTitle}
                  </CompareChartColumnTitle>
                )}
              </tr>
              {compare.rows.map((row) => (
                <React.Fragment key={row._key}>
                  <tr>
                    <CompareChartTitle
                      $isSynWk={productSku === PRODUCT_SKUS.DAILY_SYNBIOTIC}
                    >
                      {row.label}
                    </CompareChartTitle>
                    <td>
                      <CompareChartDot
                        className='checked'
                        aria-label='true'
                      ></CompareChartDot>
                    </td>
                    <td>
                      {!row.competitorOneText && (
                        <CompareChartDot
                          className={row.competitorOneChecked ? 'checked' : ''}
                          aria-label={
                            row.competitorOneChecked ? 'true' : 'false'
                          }
                        ></CompareChartDot>
                      )}
                      {!!row.competitorOneText && (
                        <ChartItemText
                          $isSynWk={productSku === PRODUCT_SKUS.DAILY_SYNBIOTIC}
                        >
                          {row.competitorOneText}
                        </ChartItemText>
                      )}
                    </td>
                    {includeThirdRow && (
                      <td>
                        {!row.competitorTwoText && (
                          <CompareChartDot
                            className={
                              row.competitorTwoChecked ? 'checked' : ''
                            }
                            aria-label={
                              row.competitorTwoChecked ? 'true' : 'false'
                            }
                          ></CompareChartDot>
                        )}
                        {!!row.competitorTwoText && (
                          <ChartItemText>{row.competitorTwoText}</ChartItemText>
                        )}
                      </td>
                    )}
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </CompareChart>
        </CompareChartScrollWrapper>
      </CompareContents>
    </CompareWrapper>
  );
};

export default ProductCompareChart;
